import { ColumnDef } from '@tanstack/react-table';
import type { TableFieldApiResponse } from '@loader/shared-types';

import {
  createTextColumn,
  createNumberColumn,
  createDateColumn,
  createBooleanColumn,
} from '@/components/data-grid';
import { logger } from '@/lib/logger';

/**
 * Generate table columns based on schema field definitions
 * Uses schema field types for accurate column rendering
 *
 * ALWAYS uses schema-defined types - never falls back to auto-detection
 */
export function generateColumnsFromSchema<
  TData extends Record<string, unknown>,
>(data: TData[], schemaFields: TableFieldApiResponse[]): ColumnDef<TData>[] {
  if (!data.length) return [];

  const firstRow = data[0];

  // Generate columns in the order specified by schemaFields, not data key order
  // This ensures columns respect the backend's field ordering (including column_order)
  return schemaFields.map((field) => {
    const key = field.name;

    // Skip fields that don't exist in the data
    if (!(key in firstRow)) {
      return null;
    }

    const headerName = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

    // Use schema field type if available
    if (field) {
      const dataType = field.data_type;

      // ONLY use schema-defined types - no auto-detection based on column names
      switch (dataType) {
        case 'boolean':
          return createBooleanColumn<TData>(key, headerName, {
            minWidth: 100,
            maxWidth: 120,
          });

        case 'date':
        case 'datetime':
          return createDateColumn<TData>(key, headerName, {
            minWidth: 150,
            maxWidth: 250,
            showTime: dataType === 'datetime', // Show time for datetime fields
          });

        case 'number':
          return createNumberColumn<TData>(key, headerName, {
            minWidth: 120,
            maxWidth: 200,
            precision: 2, // Always 2 decimal places for numbers
          });

        case 'text':
        case 'json':
        default:
          return createTextColumn<TData>(key, headerName, {
            minWidth: 180,
            maxWidth: 300,
          });
      }
    }

    // If field not in schema, default to text column (no auto-detection)
    // This can happen for calculated columns or system fields
    logger.warn('Column not found in schema, defaulting to text column', { column: key });
    return createTextColumn<TData>(key, headerName, {
      minWidth: 180,
      maxWidth: 300,
    });
  }).filter((col): col is ColumnDef<TData> => col !== null);
}

/**
 * Generate table columns dynamically based on the first row of data
 * Auto-detects data types and applies appropriate column renderers
 *
 * Use this when schema is not available (e.g., diff views, raw data views)
 */
export function generateColumnsFromData<TData extends Record<string, unknown>>(
  data: TData[]
): ColumnDef<TData>[] {
  if (!data.length) return [];

  const firstRow = data[0];
  return Object.keys(firstRow).map((key) => {
    const headerName = key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());

    return generateColumnFromValue(key, headerName, firstRow[key]);
  });
}

/**
 * Generate a column definition based on a value's type
 * Helper function for auto-detection when schema is unavailable
 */
function generateColumnFromValue<TData extends Record<string, unknown>>(
  key: string,
  headerName: string,
  value: unknown
): ColumnDef<TData> {
  const isNumeric = typeof value === 'number';
  const isBoolean = typeof value === 'boolean';

  // More strict date detection - only if it looks like an ISO date
  const isDate =
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}/.test(value) &&
    !isNaN(Date.parse(value));

  if (isBoolean) {
    return createBooleanColumn<TData>(key, headerName, {
      minWidth: 100,
      maxWidth: 120,
    });
  }

  if (isDate) {
    return createDateColumn<TData>(key, headerName, {
      minWidth: 150,
      maxWidth: 250,
    });
  }

  if (isNumeric) {
    return createNumberColumn<TData>(key, headerName, {
      minWidth: 120,
      maxWidth: 200,
      precision: 2,
    });
  }

  return createTextColumn<TData>(key, headerName, {
    minWidth: 180,
    maxWidth: 300,
  });
}

